import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Filter,
  Factory,
  Users,
  Calendar,
  Clock,
  AlertTriangle,
  ChevronRight,
  ArrowLeft,
  Boxes,
  Hammer,
  ListPlus,
  TrendingUp
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { handleApiError } from "../../utils/errorHandler";
import { useTranslation } from "../../contexts/I18nContext";

const STATI_COMMESSA = [
  { value: "aperta", label: "Aperta", color: "bg-blue-100 text-blue-800" },
  { value: "in_corso", label: "In Corso", color: "bg-yellow-100 text-yellow-800" },
  { value: "sospesa", label: "Sospesa", color: "bg-orange-100 text-orange-800" },
  { value: "completata", label: "Completata", color: "bg-green-100 text-green-800" },
  { value: "annullata", label: "Annullata", color: "bg-red-100 text-red-800" }
];

const PRIORITA = [
  { value: 1, label: "Alta", color: "destructive" },
  { value: 2, label: "Media", color: "secondary" },
  { value: 3, label: "Bassa", color: "outline" }
];

export default function ProduzioneCommessePage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const canEditProduzione = canEdit("produzione");
  
  const [commesse, setCommesse] = useState([]);
  const [stabilimenti, setStabilimenti] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStato, setFiltroStato] = useState("__all__");
  const [filtroStabilimento, setFiltroStabilimento] = useState("__all__");
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingCommessa, setEditingCommessa] = useState(null);
  const [formData, setFormData] = useState({
    codice: "",
    titolo: "",
    descrizione: "",
    cliente_nome: "",
    fornitore_id: "__none__",
    data_inizio: "",
    data_consegna_prevista: "",
    ore_budget_totali: 0,
    stato: "aperta",
    priorita: 2,
    stabilimento_id: ""
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let url = "/tenant/produzione/commesse";
      const params = new URLSearchParams();
      if (filtroStato) params.append("stato", filtroStato);
      if (filtroStabilimento) params.append("stabilimento_id", filtroStabilimento);
      if (params.toString()) url += `?${params.toString()}`;

      const [commesseRes, stabRes, fornRes] = await Promise.all([
        api.get(url),
        api.get("/tenant/produzione/stabilimenti"),
        api.get("/tenant/produzione/fornitori")
      ]);
      setCommesse(commesseRes.data);
      setStabilimenti(stabRes.data);
      setFornitori(fornRes.data);
    } catch (error) {
      
      handleApiError(error, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [filtroStato, filtroStabilimento, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingCommessa(null);
    setFormData({
      codice: "",
      titolo: "",
      descrizione: "",
      cliente_nome: "",
      fornitore_id: "__none__",
      data_inizio: new Date().toISOString().split('T')[0],
      data_consegna_prevista: "",
      ore_budget_totali: 0,
      stato: "aperta",
      priorita: 2,
      stabilimento_id: "__none__"
    });
    setShowDialog(true);
  };

  const openEditDialog = (commessa) => {
    setEditingCommessa(commessa);
    setFormData({
      codice: commessa.codice,
      titolo: commessa.titolo,
      descrizione: commessa.descrizione || "",
      cliente_nome: commessa.cliente_nome || "",
      fornitore_id: commessa.fornitore_id || "__none__",
      data_inizio: commessa.data_inizio || "",
      data_consegna_prevista: commessa.data_consegna_prevista || "",
      ore_budget_totali: commessa.ore_budget_totali || 0,
      stato: commessa.stato,
      priorita: commessa.priorita || 2,
      stabilimento_id: commessa.stabilimento_id || "__none__"
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.codice || !formData.titolo) {
      toast.error("Codice e titolo sono obbligatori");
      return;
    }

    try {
      // Convert placeholder values to null
      const dataToSave = {
        ...formData,
        stabilimento_id: formData.stabilimento_id === "__none__" ? null : formData.stabilimento_id,
        fornitore_id: formData.fornitore_id === "__none__" ? null : formData.fornitore_id
      };
      
      if (editingCommessa) {
        await api.put(`/tenant/produzione/commesse/${editingCommessa.id}`, dataToSave);
        toast.success("Commessa aggiornata");
      } else {
        await api.post("/tenant/produzione/commesse", dataToSave);
        toast.success("Commessa creata");
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t("confirm.deleteOrder"))) return;
    
    try {
      await api.delete(`/tenant/produzione/commesse/${id}`);
      toast.success("Commessa eliminata");
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Filtra commesse per ricerca e filtri
  const commesseFiltrate = commesse.filter(c => {
    const matchSearch = c.codice.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.titolo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.cliente_nome && c.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchStato = filtroStato === "__all__" || c.stato === filtroStato;
    const matchStabilimento = filtroStabilimento === "__all__" || c.stabilimento_id === filtroStabilimento;
    
    return matchSearch && matchStato && matchStabilimento;
  });

  const getStatoInfo = (stato) => STATI_COMMESSA.find(s => s.value === stato) || STATI_COMMESSA[0];
  const getPrioritaInfo = (priorita) => PRIORITA.find(p => p.value === priorita) || PRIORITA[1];

  return (
    <div className="space-y-6" data-testid="produzione-commesse-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t("production.workOrders")}</h1>
            <p className="text-muted-foreground">Gestisci progetti e commesse di produzione</p>
          </div>
        </div>
        <Button onClick={openCreateDialog} data-testid="btn-nuova-commessa">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Commessa
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("production.searchByTitle")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-commesse"
                />
              </div>
            </div>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("common.allStatuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli stati</SelectItem>
                {STATI_COMMESSA.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStabilimento} onValueChange={setFiltroStabilimento}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tutti gli stabilimenti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli stabilimenti</SelectItem>
                {stabilimenti.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista Commesse */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : commesseFiltrate.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Factory className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-4" />
            <h3 className="text-lg font-medium">{t("production.noOrders")}</h3>
            <p className="text-muted-foreground mt-1">Crea la prima commessa per iniziare</p>
            <Button className="mt-4" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Crea Commessa
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {commesseFiltrate.map((commessa) => {
            const statoInfo = getStatoInfo(commessa.stato);
            const prioritaInfo = getPrioritaInfo(commessa.priorita);
            const isInRitardo = commessa.data_consegna_prevista && 
              new Date(commessa.data_consegna_prevista) < new Date() && 
              !['completata', 'annullata'].includes(commessa.stato);

            return (
              <Card 
                key={commessa.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/${tenant}/produzione/commesse/${commessa.id}`)}
                data-testid={`commessa-card-${commessa.codice}`}
              >
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono font-bold text-lg">{commessa.codice}</span>
                        <Badge className={statoInfo.color}>{statoInfo.label}</Badge>
                        <Badge variant={prioritaInfo.color}>P{commessa.priorita}</Badge>
                        {isInRitardo && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            In Ritardo
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-medium text-lg">{commessa.titolo}</h3>
                      {commessa.descrizione && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{commessa.descrizione}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                        {commessa.cliente_nome && (
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {commessa.cliente_nome}
                          </span>
                        )}
                        {commessa.stabilimento_nome && (
                          <span className="flex items-center gap-1">
                            <Factory className="h-4 w-4" />
                            {commessa.stabilimento_nome}
                          </span>
                        )}
                        {commessa.data_consegna_prevista && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Consegna: {new Date(commessa.data_consegna_prevista).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      {/* Statistiche */}
                      <div className="text-right mr-4">
                        <div className="text-2xl font-bold">{commessa.avanzamento_percentuale?.toFixed(0) || 0}%</div>
                        <Progress 
                          value={commessa.avanzamento_percentuale || 0} 
                          className="w-32 h-2 mt-1"
                        />
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {commessa.ore_effettive?.toFixed(0) || 0}h / {commessa.ore_budget_totali?.toFixed(0) || 0}h
                          </span>
                          <span className="flex items-center gap-1">
                            <Boxes className="h-3 w-3" />
                            {commessa.materiale_ricevuto || 0}/{commessa.materiale_atteso || 0}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${tenant}/produzione/commesse/${commessa.id}`);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizza
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(commessa);
                          }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(commessa.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Crea/Modifica */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCommessa ? t("production.editOrder") : t("production.newOrder")}</DialogTitle>
            <DialogDescription>
              {editingCommessa ? t("production.editOrder") : t("production.newOrder")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="codice">Codice *</Label>
              <Input
                id="codice"
                placeholder="es. COMM-001"
                value={formData.codice}
                onChange={(e) => setFormData({...formData, codice: e.target.value})}
                data-testid="input-codice"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="titolo">Titolo *</Label>
              <Input
                id="titolo"
                placeholder="Nome della commessa"
                value={formData.titolo}
                onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                data-testid="input-titolo"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="descrizione">{t("common.description")}</Label>
              <Textarea
                id="descrizione"
                placeholder="Descrizione dettagliata..."
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cliente">Cliente</Label>
              <Input
                id="cliente"
                placeholder="Nome cliente"
                value={formData.cliente_nome}
                onChange={(e) => setFormData({...formData, cliente_nome: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fornitore">Fornitore</Label>
              <Select
                value={formData.fornitore_id}
                onValueChange={(v) => setFormData({...formData, fornitore_id: v})}
              >
                <SelectTrigger data-testid="select-fornitore">
                  <SelectValue placeholder="Seleziona fornitore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("common.none")}</SelectItem>
                  {fornitori.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.ragione_sociale}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stabilimento">Stabilimento</Label>
              <Select 
                value={formData.stabilimento_id} 
                onValueChange={(v) => setFormData({...formData, stabilimento_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona stabilimento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("common.none")}</SelectItem>
                  {stabilimenti.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_inizio">Data Inizio</Label>
              <Input
                id="data_inizio"
                type="date"
                value={formData.data_inizio}
                onChange={(e) => setFormData({...formData, data_inizio: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_consegna">Data Consegna Prevista</Label>
              <Input
                id="data_consegna"
                type="date"
                value={formData.data_consegna_prevista}
                onChange={(e) => setFormData({...formData, data_consegna_prevista: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ore_budget">Ore Budget Totali</Label>
              <Input
                id="ore_budget"
                type="number"
                min="0"
                step="0.5"
                value={formData.ore_budget_totali}
                onChange={(e) => setFormData({...formData, ore_budget_totali: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priorita">Priorita</Label>
              <Select 
                value={String(formData.priorita)} 
                onValueChange={(v) => setFormData({...formData, priorita: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITA.map(p => (
                    <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stato">{t("common.status")}</Label>
              <Select 
                value={formData.stato} 
                onValueChange={(v) => setFormData({...formData, stato: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATI_COMMESSA.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave} data-testid="btn-salva-commessa">
              {editingCommessa ? "Aggiorna" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
